import React, { useEffect, useMemo, useState } from "react";

import * as Yup from "yup";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import {
  MessageSquare,
  Mail,
  User,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Building2,
  Check,
  Wrench,
  ShieldAlert,
  Ban,
  Clock,
} from "lucide-react";

import AuthLayout from "../../components/AuthLayout";
import DocumentoFiscal from "../../components/DocumentoFiscal";
import PhoneInput from "../../components/PhoneInput";
// Mesma regra de senha usada nos modais e no backend (helpers/ValidarSenha.ts).
import SenhaForte, { avaliarSenha } from "../../components/SenhaForte";
import useMoedaAtiva, { formatarMoeda } from "../../hooks/useMoedaAtiva";
import usePaisFiscal, {
  cepCompleto,
  documentoCompleto,
  regrasVigentes,
  mascararCep,
  somenteDigitos,
} from "../../hooks/usePaisFiscal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// O cadastro cria a empresa, o administrador dela e a assinatura. Por isso pede
// CPF/CNPJ, telefone, CEP e número: sem esses dados o Asaas recusa a cobrança e
// a conta nasceria sem como pagar. Como é muita coisa para uma tela só, vai em
// duas etapas — acesso primeiro, cobrança depois.

// O documento e o código postal mudam de país para país — o mesmo formulário
// pede CPF/CNPJ cobrando em real e NIF cobrando em euro. Por isso o schema é
// montado com as regras vindas do servidor, e não fixo no arquivo.
const montarSchema = (regras) =>
  Yup.object().shape({
    adminName: Yup.string()
      .min(2, "Muito curto!")
      .max(50, "Muito longo!")
      .required("Obrigatório"),
    adminEmail: Yup.string().email("E-mail inválido").required("Obrigatório"),
    // A regra de senha vem de components/SenhaForte (espelho do helper do
    // backend). O cadastro público não pode aceitar senha mais fraca que o
    // resto do sistema: o backend revalida e devolve ERR_WEAK_PASSWORD.
    adminPassword: Yup.string()
      .required("Obrigatório")
      .test(
        "senha-forte",
        "Senha fraca",
        function validarSenhaDoCadastro(valor) {
          const { adminName, adminEmail } = this.parent || {};
          const avaliacao = avaliarSenha(valor, {
            nome: adminName,
            email: adminEmail,
          });
          return avaliacao.ok || this.createError({ message: avaliacao.erro });
        },
      ),
    companyName: Yup.string().min(2, "Muito curto!").required("Obrigatório"),
    // O documento é conferido pelas regras do país escolhido no campo — que
    // é o da plataforma enquanto ninguém marcar "cliente estrangeiro".
    document: Yup.string()
      .required("Obrigatório")
      .when("documentCountry", ([pais], schema) => {
        const vigente = regrasVigentes(regras, pais);

        return schema.test(
          "tamanho",
          `${vigente.rotuloDocumento} incompleto`,
          (v) => documentoCompleto(v, vigente),
        );
      }),
    email: Yup.string().email("E-mail inválido").required("Obrigatório"),
    // Oito dígitos era o mínimo do celular brasileiro com DDD. Número de fora
    // é mais curto: com o DDI, um fixo português tem 12 no total.
    phone: Yup.string()
      .required("Obrigatório")
      .test(
        "tamanho",
        "Telefone incompleto",
        (v) => somenteDigitos(v).length >= 8,
      ),
    // O endereço de cobrança fica no país do cliente: exigir CEP de 8 dígitos
    // de quem mora em Portugal travaria o cadastro no último campo.
    postalCode: Yup.string()
      .required("Obrigatório")
      .when("documentCountry", ([pais], schema) => {
        const vigente = regrasVigentes(regras, pais);

        return schema.test(
          "tamanho",
          `${vigente.rotuloCep} incompleto`,
          (v) => cepCompleto(v, vigente),
        );
      }),
    addressNumber: Yup.string().required("Obrigatório"),
  });

// Campos da primeira etapa — validados sozinhos antes de avançar. Sem isso o
// botão "Continuar" reclamaria de campos que a pessoa ainda nem viu.
const CAMPOS_ETAPA_1 = ["adminName", "adminEmail", "adminPassword"];

const formatarDataHora = (valor) => {
  if (!valor) return "—";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(data);
};

const SignUp = () => {
  const navigate = useNavigate();

  const [etapa, setEtapa] = useState(1);
  const [planos, setPlanos] = useState([]);
  const [planoEscolhido, setPlanoEscolhido] = useState(null);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [maintenance, setMaintenance] = useState(null);
  const regras = usePaisFiscal();
  // A landing mostrava R$ mesmo com a cobrança em euro.
  const moeda = useMoedaAtiva();
  const schema = useMemo(() => montarSchema(regras), [regras]);

  const initialState = {
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    companyName: "",
    document: "",
    documentCountry: "",
    email: "",
    phone: "",
    postalCode: "",
    address: "",
    addressNumber: "",
    addressComplement: "",
    province: "",
    city: "",
    state: "",
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/public-plans");
        setPlanos(Array.isArray(data) ? data : []);
      } catch (err) {
        // Sem planos à venda a tela continua funcionando: a empresa nasce sem
        // plano e o dono do SaaS define depois.
        setPlanos([]);
      }
    })();
  }, []);

  useEffect(() => {
    let vivo = true;

    api
      .get("/auth/maintenance")
      .then(({ data }) => {
        if (!vivo) return;
        setMaintenance(data || null);
      })
      .catch(() => {
        if (!vivo) return;
        setMaintenance(null);
      });

    return () => {
      vivo = false;
    };
  }, []);

  // Preenche rua, bairro e cidade a partir do CEP. É conveniência: se a
  // consulta falhar, os campos seguem editáveis e o cadastro não trava.
  //
  // Só no Brasil: o ViaCEP é uma base de CEP brasileiro e responderia "não
  // encontrado" para todo código postal de fora, gastando uma ida à rede a
  // cada tecla.
  const buscarCep = async (cep, setFieldValue, vigente) => {
    if ((vigente || regras).pais !== "BR") return;

    const digitos = somenteDigitos(cep);

    if (digitos.length !== 8) return;

    setBuscandoCep(true);

    try {
      const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`);
      const dados = await resposta.json();

      if (!dados.erro) {
        setFieldValue("address", dados.logradouro || "");
        setFieldValue("province", dados.bairro || "");
        setFieldValue("city", dados.localidade || "");
        setFieldValue("state", dados.uf || "");
      }
    } catch (err) {
      // silencioso de propósito
    } finally {
      setBuscandoCep(false);
    }
  };

  const handleSignUp = async (values) => {
    try {
      await api.post("/auth/register-company", {
        ...values,
        document: somenteDigitos(values.document),
        phone: somenteDigitos(values.phone),
        postalCode: somenteDigitos(values.postalCode),
        planId: planoEscolhido,
      });
      toast.success(i18n.t("signup.toasts.success"));
      navigate("/login");
    } catch (err) {
      toastError(err);
    }
  };

  const avancar = async (validateForm, setTouched) => {
    const erros = await validateForm();
    const errosDaEtapa = CAMPOS_ETAPA_1.filter((campo) => erros[campo]);

    if (errosDaEtapa.length) {
      setTouched(
        CAMPOS_ETAPA_1.reduce((acc, campo) => ({ ...acc, [campo]: true }), {}),
      );
      return;
    }

    setEtapa(2);
  };

  const erroDe = (touched, errors, nome) =>
    touched[nome] && errors[nome] ? (
      <p className="text-xs text-destructive">{errors[nome]}</p>
    ) : null;

  if (maintenance?.isActive) {
    return (
      <AuthLayout>
        <div className="mx-auto w-full max-w-xl">
          <div className="overflow-hidden rounded-3xl border border-amber-200/80 bg-gradient-to-b from-amber-50 via-white to-white shadow-xl">
            <div className="border-b border-amber-200/80 bg-amber-100/70 px-6 py-6">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white text-amber-700 shadow-sm">
                  <Wrench className="h-7 w-7" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    Cadastro temporariamente indisponível
                  </h1>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    O sistema está em manutenção no momento. Durante esta janela,
                    a criação de novas contas e novas adesões fica bloqueada até a
                    normalização do ambiente.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-5 px-6 py-6">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-amber-100 bg-white px-4 py-4 shadow-sm">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Clock className="h-4 w-4 text-amber-700" />
                    Início
                  </div>
                  <p className="mt-2 text-sm text-slate-600">
                    {formatarDataHora(maintenance?.startsAt)}
                  </p>
                </div>

                <div className="rounded-2xl border border-amber-100 bg-white px-4 py-4 shadow-sm">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Clock className="h-4 w-4 text-amber-700" />
                    Previsão de término
                  </div>
                  <p className="mt-2 text-sm text-slate-600">
                    {formatarDataHora(maintenance?.endsAt)}
                  </p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-amber-100 bg-white px-4 py-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <Ban className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        Novos cadastros pausados
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-slate-600">
                        Não é possível criar contas, empresas ou iniciar assinatura
                        enquanto a manutenção estiver ativa.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-amber-100 bg-white px-4 py-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        Estabilidade operacional
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-slate-600">
                        Esta pausa existe para garantir atualização segura e evitar
                        inconsistências durante a janela técnica.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {maintenance?.message ? (
                <div className="rounded-2xl border border-amber-100 bg-white px-4 py-4 shadow-sm">
                  <p className="text-sm font-semibold text-slate-900">
                    Comunicado da manutenção
                  </p>
                  <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">
                    {maintenance.message}
                  </p>
                </div>
              ) : null}

              <div className="flex flex-col gap-3 sm:flex-row">
                <RouterLink to="/login" className="flex-1">
                  <Button type="button" className="h-11 w-full">
                    Entrar no sistema
                  </Button>
                </RouterLink>
                <Button
                  type="button"
                  variant="outline"
                  disabled
                  className="h-11 flex-1 cursor-not-allowed border-amber-200 bg-amber-50 text-amber-700 opacity-100"
                >
                  Cadastro indisponível
                </Button>
              </div>
            </div>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* logo mobile */}
      <div className="mb-8 flex items-center gap-3 lg:hidden">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary shadow-lg shadow-primary/30">
          <MessageSquare className="h-6 w-6 text-white" />
        </div>
        <p className="text-lg font-bold tracking-tight">Perabit</p>
      </div>

      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Crie sua conta</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {etapa === 1
            ? "Comece agora a organizar seu atendimento no WhatsApp."
            : "Agora os dados da empresa, usados na sua fatura."}
        </p>
      </div>

      {/* As duas etapas ditas em voz alta: ninguém gosta de descobrir no meio
          que o formulário era maior do que parecia. */}
      <div className="mb-7 flex items-center gap-3">
        {[
          { numero: 1, rotulo: "Seu acesso" },
          { numero: 2, rotulo: "Sua empresa" },
        ].map((passo, indice) => (
          <React.Fragment key={passo.numero}>
            {indice > 0 && <span className="h-px flex-1 bg-border" />}
            <span className="flex items-center gap-2">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  etapa >= passo.numero
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {etapa > passo.numero ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  passo.numero
                )}
              </span>
              <span
                className={`text-xs font-medium ${
                  etapa === passo.numero
                    ? "text-foreground"
                    : "text-muted-foreground"
                }`}
              >
                {passo.rotulo}
              </span>
            </span>
          </React.Fragment>
        ))}
      </div>

      <Formik
        initialValues={initialState}
        validationSchema={schema}
        onSubmit={(values, actions) => {
          handleSignUp(values).finally(() => actions.setSubmitting(false));
        }}
      >
        {({
          touched,
          errors,
          values,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          validateForm,
          setTouched,
        }) => (
          <Form className="space-y-5" noValidate>
            {etapa === 1 ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="adminName">Seu nome</Label>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Field
                      as={Input}
                      id="adminName"
                      name="adminName"
                      autoComplete="name"
                      autoFocus
                      placeholder="Seu nome completo"
                      className="h-11 pl-10"
                    />
                  </div>
                  {erroDe(touched, errors, "adminName")}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminEmail">E-mail de acesso</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Field
                      as={Input}
                      id="adminEmail"
                      name="adminEmail"
                      type="email"
                      autoComplete="email"
                      placeholder="seu@email.com"
                      className="h-11 pl-10"
                    />
                  </div>
                  {erroDe(touched, errors, "adminEmail")}
                </div>

                <div className="space-y-2">
                  {/* A senha do cadastro segue a mesma regra do resto do
                      sistema: medidor, checklist e gerador no próprio campo. */}
                  <SenhaForte
                    id="adminPassword"
                    label="Senha"
                    value={values.adminPassword || ""}
                    onChange={(valor) => setFieldValue("adminPassword", valor)}
                    onBlur={() => setFieldTouched("adminPassword", true)}
                    nome={values.adminName}
                    email={values.adminEmail}
                    touched={touched.adminPassword}
                    error={errors.adminPassword}
                    autoComplete="new-password"
                    placeholder="Crie uma senha forte"
                  />
                </div>

                <Button
                  type="button"
                  onClick={() => avancar(validateForm, setTouched)}
                  className="h-11 w-full text-[15px] font-semibold"
                >
                  Continuar
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="companyName">Nome da empresa</Label>
                  <div className="relative">
                    <Building2 className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Field
                      as={Input}
                      id="companyName"
                      name="companyName"
                      autoFocus
                      placeholder="Como sua empresa se chama"
                      className="h-11 pl-10"
                    />
                  </div>
                  {erroDe(touched, errors, "companyName")}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <DocumentoFiscal
                      paisSelecionado={values.documentCountry}
                      onPaisChange={(p) => setFieldValue("documentCountry", p)}
                      valor={values.document}
                      onValorChange={(v) => setFieldValue("document", v)}
                      erro={touched.document && errors.document}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone</Label>
                    <PhoneInput
                      id="phone"
                      name="phone"
                      className="[&_input]:h-11 [&>button]:h-11"
                      paisPadrao={regrasVigentes(regras, values.documentCountry).pais}
                      value={values.phone}
                      onChange={(numero) => setFieldValue("phone", numero)}
                    />
                    {erroDe(touched, errors, "phone")}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail de cobrança</Label>
                  <Field
                    as={Input}
                    id="email"
                    name="email"
                    type="email"
                    placeholder="financeiro@suaempresa.com"
                    className="h-11"
                  />
                  <p className="text-xs text-muted-foreground">
                    É para onde a fatura vai. Pode ser o mesmo do seu acesso.
                  </p>
                  {erroDe(touched, errors, "email")}
                </div>

                <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
                  <div className="space-y-2">
                    <Label htmlFor="postalCode">
                      {regrasVigentes(regras, values.documentCountry).rotuloCep}
                    </Label>
                    <div className="relative">
                      <Input
                        id="postalCode"
                        name="postalCode"
                        inputMode="numeric"
                        placeholder={
                          regrasVigentes(regras, values.documentCountry)
                            .exemploCep || "00000-000"
                        }
                        className="h-11"
                        value={values.postalCode}
                        onChange={(e) => {
                          const vigente = regrasVigentes(
                            regras,
                            values.documentCountry,
                          );
                          const cep = mascararCep(e.target.value, vigente);
                          setFieldValue("postalCode", cep);
                          buscarCep(cep, setFieldValue, vigente);
                        }}
                      />
                      {buscandoCep && (
                        <Loader2 className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    {erroDe(touched, errors, "postalCode")}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="addressNumber">Número</Label>
                    <Field
                      as={Input}
                      id="addressNumber"
                      name="addressNumber"
                      placeholder="123"
                      className="h-11"
                    />
                    {erroDe(touched, errors, "addressNumber")}
                  </div>
                </div>

                {(values.address || values.city) && (
                  <p className="-mt-1 text-xs text-muted-foreground">
                    {[values.address, values.province, values.city, values.state]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}

                <div className="space-y-2">
                  <Label htmlFor="addressComplement">
                    Complemento{" "}
                    <span className="font-normal text-muted-foreground">
                      (opcional)
                    </span>
                  </Label>
                  <Field
                    as={Input}
                    id="addressComplement"
                    name="addressComplement"
                    placeholder="Sala 4, fundos…"
                    className="h-11"
                  />
                </div>

                {planos.length > 0 && (
                  <div className="space-y-2">
                    <Label>Plano</Label>
                    <div className="grid gap-2">
                      {planos.map((plano) => (
                        <button
                          key={plano.id}
                          type="button"
                          onClick={() => setPlanoEscolhido(plano.id)}
                          className={`flex items-center justify-between rounded-lg border p-3 text-left transition-colors ${
                            planoEscolhido === plano.id
                              ? "border-primary bg-primary/5"
                              : "hover:bg-muted/50"
                          }`}
                        >
                          <span>
                            <span className="block text-sm font-medium">
                              {plano.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {plano.users} usuários · {plano.connections}{" "}
                              conexões
                            </span>
                          </span>
                          <span className="text-sm font-semibold">
                            {formatarMoeda(plano.value, moeda)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEtapa(1)}
                    className="h-11"
                    disabled={isSubmitting}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    className="h-11 flex-1 text-[15px] font-semibold"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        {i18n.t("signup.buttons.submit")}
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>

      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-background px-3 text-xs uppercase tracking-wider text-muted-foreground">
            Já tem uma conta?
          </span>
        </div>
      </div>

      <RouterLink to="/login" className="block">
        <Button type="button" variant="outline" className="h-11 w-full">
          Entrar na minha conta
        </Button>
      </RouterLink>
    </AuthLayout>
  );
};

export default SignUp;
